/**
 * Generic cloud function client for loading timeseries data from PostgreSQL.
 * Uses the loadTimeseries cloud function via Firebase compat SDK.
 */

import firebase from 'firebase/compat/app';
import 'firebase/compat/functions';
import 'firebase/compat/firestore';
import { waitForAuth } from './authUtils';
import { SeriesConfig } from './seriesConfig';
import { autoResolutionMinutes, TIMESERIES_RESOLUTION_STEPS } from './seriesUtils';
import type { SeriesPerformanceDoc } from './seriesPerformance';

/** Raw row returned by the loadTimeseries cloud function. */
export interface TimeseriesRow {
    timestamp: string;
    data: Record<string, any>;
    series: string;
    name: string;
    source: string;
    entity: string;
    type: string;
    period: string;
}

interface LoadTimeseriesResponse {
    rows: TimeseriesRow[];
    truncated: boolean;
    intervalMinutes: number | null;
}

export interface LoadTimeseriesOptions {
    from?: Date;
    to?: Date;
    signal?: AbortSignal;
}

type ParsedRow = { timestamp: string; data: Record<string, any> };

// Nanosecond timestamp for year 2100
const FAR_FUTURE_NS = 4102444800000000000;

function dateToNanos(date: Date): number {
    return date.getTime() * 1_000_000;
}

/**
 * Parse nanosecond timestamp string to ISO date string.
 * The calculation code in clustering/strategy expects string timestamps
 * that can be passed to `new Date(timestamp)`.
 */
function parseNanosToISOString(timestampStr: string): string {
    if (/[A-Za-z-]/.test(timestampStr)) {
        return timestampStr; // Already ISO format
    }
    const nanos = BigInt(timestampStr);
    const millis = nanos / BigInt(1_000_000);
    return new Date(Number(millis)).toISOString();
}

interface PageResult {
    rows: ParsedRow[];
    truncated: boolean;
    /** Raw nanosecond timestamp of the last row (for cursor pagination). */
    lastRawTimestamp: string | null;
}

function parsePage(response: LoadTimeseriesResponse): PageResult {
    const rows = response.rows.map(row => ({
        timestamp: parseNanosToISOString(row.timestamp),
        data: row.data,
    }));
    const lastRaw = response.rows.length > 0
        ? response.rows[response.rows.length - 1].timestamp
        : null;
    return { rows, truncated: response.truncated, lastRawTimestamp: lastRaw };
}

/** Single page request to the loadTimeseries cloud function. */
async function fetchPage(
    series: string,
    fromNs: number,
    toNs: number,
    intervalMinutes: number,
    signal?: AbortSignal,
): Promise<PageResult> {
    const functions = firebase.app().functions('europe-west6');
    const callable = functions.httpsCallable('loadTimeseries');

    if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');

    const payload: Record<string, any> = { series, from: fromNs, to: toNs };
    if (intervalMinutes > 0) {
        payload.intervalMinutes = intervalMinutes;
    }

    const result = await callable(payload);

    if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');

    return parsePage(result.data as LoadTimeseriesResponse);
}

/**
 * Client-side downsampling: keep one row per time bucket.
 * Acts as a safety net until server-side intervalMinutes is deployed,
 * after which the server already returns fewer rows and this is a no-op.
 */
function downsampleRows(rows: ParsedRow[], intervalMinutes: number): ParsedRow[] {
    if (intervalMinutes <= 0 || rows.length === 0) return rows;
    const intervalMs = intervalMinutes * 60_000;
    const result: ParsedRow[] = [rows[0]];
    let lastBucket = Math.floor(new Date(rows[0].timestamp).getTime() / intervalMs);
    for (let i = 1; i < rows.length; i++) {
        const bucket = Math.floor(new Date(rows[i].timestamp).getTime() / intervalMs);
        if (bucket !== lastBucket) {
            result.push(rows[i]);
            lastBucket = bucket;
        }
    }
    return result;
}

// Safety limit to avoid runaway pagination loops.
const MAX_PAGES = 50;

/**
 * Load timeseries data from PostgreSQL via the loadTimeseries cloud function.
 *
 * Uses cursor-based pagination: when the server returns `truncated: true`,
 * the last row's timestamp is used as the `from` for the next page. This
 * works with any data density without guessing chunk sizes.
 *
 * When the cloud function supports `intervalMinutes` (server-side
 * downsampling), it is passed automatically based on the date range,
 * reducing total data transfer and page count.
 */
export async function loadTimeseries(
    series: string,
    options?: LoadTimeseriesOptions,
): Promise<ParsedRow[]> {
    await waitForAuth();

    const signal = options?.signal;
    const toNs = options?.to ? dateToNanos(options.to) : FAR_FUTURE_NS;
    let cursorNs = options?.from ? dateToNanos(options.from) : 0;

    // Pass intervalMinutes for when the server supports downsampling.
    // The deployed server ignores unknown fields, so this is safe.
    const intervalMinutes = (options?.from && options?.to)
        ? autoResolutionMinutes(
            [options.from, options.to],
            TIMESERIES_RESOLUTION_STEPS,
            1440,
        )
        : 0;

    const allRows: ParsedRow[] = [];

    for (let page = 0; page < MAX_PAGES; page++) {
        const result = await fetchPage(series, cursorNs, toNs, intervalMinutes, signal);
        allRows.push(...result.rows);

        if (!result.truncated || !result.lastRawTimestamp) break;

        // Advance cursor past the last returned timestamp to avoid duplicates.
        // Timestamps are nanoseconds stored as strings.
        cursorNs = Number(BigInt(result.lastRawTimestamp) + 1n);
    }

    const sorted = allRows.sort((a, b) =>
        new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime(),
    );

    return downsampleRows(sorted, intervalMinutes);
}

/** Row stored in the Firestore series catalog. */
interface CatalogSeriesRow {
    name: string;
    series: string;
    source: string;
}

interface SeriesCatalog {
    updatedAt: string;
    clustering: CatalogSeriesRow[];
    strategy: CatalogSeriesRow[];
}

/** Known algorithm abbreviations for human-readable clustering names. */
const ALGORITHM_NAMES: Record<string, string> = {
    agg: 'Agglomerative',
    agglomerative: 'Agglomerative',
    bis_kmeans: 'Bisecting K-Means',
    bis_k_means: 'Bisecting K-Means',
    bisecting_k_means: 'Bisecting K-Means',
    kmeans: 'K-Means',
    k_means: 'K-Means',
    dbscan: 'DBSCAN',
    spectral: 'Spectral',
    hierarchical: 'Hierarchical',
    birch: 'Birch',
    diana: 'DIANA',
    dip_means: 'Dip-Means',
    g_means: 'G-Means',
    lda_kmeans: 'LDA K-Means',
    lda_k_means: 'LDA K-Means',
    bull_bear: 'Bull/Bear',
    btc_20_week_moving_average: '20W Moving Average',
};

/**
 * Parse a clustering name like "agg_4_btc" into "Agglomerative 4 BTC".
 * Falls back to title-casing the raw name if the pattern doesn't match.
 */
function formatClusteringName(name: string): string {
    const sortedKeys = Object.keys(ALGORITHM_NAMES).sort((a, b) => b.length - a.length);
    for (const key of sortedKeys) {
        if (name.startsWith(key + '_')) {
            const rest = name.slice(key.length + 1);
            const parts = rest.split('_');
            const formatted = parts.map(p => /^\d+$/.test(p) ? p : p.toUpperCase()).join(' ');
            return `${ALGORITHM_NAMES[key]} ${formatted}`;
        }
        if (name === key) {
            return ALGORITHM_NAMES[key];
        }
    }
    return name.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

/** Category labels for strategy name prefixes. */
const STRATEGY_CATEGORIES: Record<string, string> = {
    clustering: 'Clustering',
    screening: 'Screening',
    rotation: 'Rotation',
    indicator: 'Indicator',
    ranking: 'Ranking',
};

/**
 * Extract the category from a strategy name.
 * e.g. "clustering:agg_4_btc" → "Clustering"
 *      "rotation:binance:usdc_v1:..." → "Rotation"
 */
export function getStrategyCategory(name: string): string {
    const prefix = name.split(':')[0];
    return STRATEGY_CATEGORIES[prefix] || 'Other';
}

/**
 * Format a single segment of a strategy name.
 * Handles clustering, screening, rotation, indicator, ranking prefixes.
 */
function formatSegment(part: string): string {
    const [prefix, ...rest] = part.split(':');

    if (prefix === 'clustering') {
        return formatClusteringName(rest[0] || '');
    }
    if (prefix === 'cluster_top_1') {
        return 'Top 1';
    }
    if (['screening', 'rotation', 'indicator', 'ranking'].includes(prefix)) {
        return rest.map(r => r.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())).join(' : ');
    }
    return part.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

/**
 * Parse a strategy name into a readable format.
 * Pipe-separated segments are joined with " | ".
 */
function formatStrategyName(name: string): string {
    return name.split('|').map(formatSegment).join(' | ');
}

/** Session-level cache for listSeries results. */
const seriesCache = new Map<string, SeriesConfig[]>();

/** Firestore catalog document cache. */
let catalogCache: SeriesCatalog | null = null;

/** Series category to name formatter mapping. */
const SERIES_FORMAT_CONFIG: Record<string, (name: string) => string> = {
    clustering: formatClusteringName,
    strategy: formatStrategyName,
};

/** Session cache for config/seriesPerformance. `undefined` means "not read
 *  yet", `null` means "read and unavailable". */
let performanceCache: SeriesPerformanceDoc | null | undefined;

/**
 * Read the cached series performance written daily by the indexer job.
 *
 * Never throws, unlike readCatalog: the indicator chart must keep working when
 * this document is missing or the job has never run, so failures resolve to
 * null and the UI renders "—" for the figures.
 */
export async function readSeriesPerformance(): Promise<SeriesPerformanceDoc | null> {
    if (performanceCache !== undefined) return performanceCache;
    try {
        await waitForAuth();
        const doc = await firebase
            .firestore()
            .collection('config')
            .doc('seriesPerformance')
            .get();
        performanceCache = doc.exists
            ? (doc.data() as SeriesPerformanceDoc)
            : null;
    } catch (err) {
        console.error('Failed to read seriesPerformance', err);
        performanceCache = null;
    }
    return performanceCache;
}

/**
 * Read the series catalog from Firestore.
 * Caches the full document so both clustering and strategy reads hit Firestore once.
 */
async function readCatalog(): Promise<SeriesCatalog> {
    if (catalogCache) return catalogCache;

    // config/* is readable only by an approved, signed-in user. Without this
    // the read can fire before auth hydrates and fail as a rules denial that
    // surfaces as the misleading "catalog not found" below. Mirrors
    // readSeriesPerformance(), which already waits.
    await waitForAuth();

    const doc = await firebase.firestore().collection('config').doc('seriesCatalog').get();

    if (!doc.exists) {
        throw new Error('Series catalog not found. Run refreshSeriesCatalog cloud function first.');
    }

    catalogCache = doc.data() as SeriesCatalog;
    return catalogCache;
}

/**
 * List available series by category from Firestore catalog.
 * Results are cached for the session.
 */
export async function listSeries(category: 'clustering' | 'strategy'): Promise<SeriesConfig[]> {
    const cached = seriesCache.get(category);
    if (cached) return cached;

    await waitForAuth();
    const catalog = await readCatalog();
    const rows = catalog[category] || [];
    const formatName = SERIES_FORMAT_CONFIG[category];

    const configs: SeriesConfig[] = rows.map(row => ({
        id: row.name,
        name: formatName(row.name),
        series: row.series,
    }));

    seriesCache.set(category, configs);
    return configs;
}
